import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { message } from 'antd';
import { useGroupCredentialingEnrollment } from 'hooks/credentialing/useGroupCredentialingEnrollment';
import {
    getCredPayoutAccount,
    getGroupCredentialingEnrollment,
    getPayerRequirements,
    saveGroupCredentialingEnrollment,
} from 'api/credentialing';
import {
    MedicaidEnrollmentView,
    MedicaidGroupFields,
    MedicaidGroupStatesView,
    entityHasAgreement,
    medicaidGroupMissing,
} from 'components/CredentialingPayerSteps';
import type { PracticeRequirementsDto } from 'components/CredentialingPayerSteps';
import { encodeEnrollmentEntityType } from 'pages/GroupCredentialingPage/constants';
import type { OrgCredentialingEnrollmentVo } from 'types/credentialing';
import ReviewSection from '../ReviewSection';
import s from '../../s.module.less';

export interface MedicaidGroupSectionHandle {
    // group review 提交校验不通过时强制展开编辑
    openEdit: () => void;
}

interface Props {
    // group review：漏答的州标红、计入 Submit 拦截（Settings 不传）
    hasErrors?: boolean;
    footerOffset?: boolean;
    actionsInHeader?: boolean;
    onEditingChange?: (editing: boolean) => void;
    // 保存成功后通知宿主刷新它自己的 enrollment（hook 的 state 按组件各存一份）
    onSaved?: () => void;
}

// practice payers 里 Medicaid 所在的州（两字母州码）
export const groupMedicaidStates = (enrollment?: OrgCredentialingEnrollmentVo | null): string[] =>
    Array.from(new Set((enrollment?.payers || []).filter((p) => /medicaid/i.test(p.payerName)).map((p) => p.state)));

// 原型 mcdGroupReviewHTML / mcdGroupSettingsHTML（10/19）：Set up your practice 里按州答的 group Medicaid
// 注册与 practice 级问卷，在 group Review 与 Settings 的 Practice enrollment tab 回显，行内编辑用同一套字段。
// Review 上没答的州标红并拦 Submit（Set up your practice 不拦 Next，漏答的在这里兜住）
const MedicaidGroupSection = forwardRef<MedicaidGroupSectionHandle, Props>(
    ({ hasErrors, footerOffset, actionsInHeader, onEditingChange, onSaved }, ref) => {
        const [enrollment, , refetchEnrollment] = useGroupCredentialingEnrollment();
        const [editing, setEditing] = useState(false);
        const [saving, setSaving] = useState(false);
        const [draft, setDraft] = useState<PracticeRequirementsDto | null>(null);
        const states = useMemo(() => groupMedicaidStates(enrollment), [enrollment]);

        // 预填依据（group 版只用 16b 与 25）
        const [ownerEverEnrolled, setOwnerEverEnrolled] = useState(false);
        const [payoutOnFile, setPayoutOnFile] = useState(false);
        useEffect(() => {
            if (!states.length) return;
            getPayerRequirements()
                .then((res: any) =>
                    setOwnerEverEnrolled((res?.data?.data?.medicaid || []).some((m: any) => m?.enrolled === true)),
                )
                .catch(() => undefined);
            getCredPayoutAccount()
                .then((res: any) => setPayoutOnFile(!!res?.data?.data?.accountNumber))
                .catch(() => undefined);
        }, [states.length]);

        useEffect(() => {
            onEditingChange?.(editing);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [editing]);

        const openEdit = () => {
            setDraft(enrollment?.practiceRequirements || {});
            setEditing(true);
        };
        useImperativeHandle(ref, () => ({
            openEdit: () => {
                if (!editing) openEdit();
            },
        }));

        // /group/enrollment 整份覆盖 billing 字段：先拉最新 detail 打底（同页 Practice enrollment 板块
        // 可能刚改过），只换 practiceRequirements
        const save = async () => {
            if (!enrollment) return;
            setSaving(true);
            try {
                let latest: OrgCredentialingEnrollmentVo = enrollment;
                try {
                    const fresh: any = await getGroupCredentialingEnrollment();
                    if (fresh?.data?.data) latest = fresh.data.data;
                } catch (e) {
                    // ignore：拉不到用手上的
                }
                const res: any = await saveGroupCredentialingEnrollment(
                    encodeEnrollmentEntityType({ ...latest, practiceRequirements: draft }),
                );
                if (res && !res.error) {
                    await refetchEnrollment();
                    onSaved?.();
                    setEditing(false);
                } else {
                    message.error(res?.error || 'Failed to save Medicaid group enrollment');
                }
            } finally {
                setSaving(false);
            }
        };

        if (!states.length) return null;
        const reqs = enrollment?.practiceRequirements;
        const anyNo = states.some((st) => (reqs?.medicaid || []).find((m) => m.state === st)?.enrolled === false);

        return (
            <ReviewSection
                sectionKey="medicaid-group"
                title="Medicaid group enrollment"
                editing={editing}
                saving={saving}
                hasErrors={hasErrors}
                footerOffset={footerOffset}
                actionsInHeader={actionsInHeader}
                onEdit={openEdit}
                onCancel={() => setEditing(false)}
                onSave={save}
                viewContent={
                    <>
                        <MedicaidGroupStatesView states={states} value={reqs} />
                        {anyNo && (
                            <div className={s.mcdGroupAnswers}>
                                <MedicaidEnrollmentView
                                    value={reqs?.medicaidEnrollment}
                                    variant="group"
                                    isCA={states.includes('CA')}
                                    showOrgAgreement={entityHasAgreement(enrollment?.entityType)}
                                />
                            </div>
                        )}
                    </>
                }
            >
                <MedicaidGroupFields
                    states={states}
                    value={draft}
                    onChange={setDraft}
                    showOrgAgreement={entityHasAgreement(enrollment?.entityType)}
                    derive={{
                        everEnrolled: ownerEverEnrolled || (draft?.medicaid || []).some((m) => m.enrolled === true),
                        payoutOnFile,
                    }}
                />
            </ReviewSection>
        );
    },
);

export default MedicaidGroupSection;

// group review 的 Submit 拦截用：某州没答 / Yes 没填 group ID
export const groupMedicaidMissing = (enrollment?: OrgCredentialingEnrollmentVo | null) =>
    medicaidGroupMissing(groupMedicaidStates(enrollment), enrollment?.practiceRequirements);
